import { useState, useEffect } from 'react';
import { dashboardApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { DollarSign, Fuel, Wrench, TrendingUp, Truck, Scissors } from 'lucide-react';

export function CostsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await dashboardApi.getCosts();
      setData(res.data);
    } catch {
      toast.error('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="costs-loading" />;

  const maxMonthlyTotal = Math.max(...(data.monthly || []).map(m => m.total), 1);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="costs-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Cost Tracking</h1>
        <p className="text-muted-foreground">Fuel, maintenance, and operational cost analytics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Costs</p>
                <p className="text-2xl font-heading font-bold" data-testid="total-costs">${data.total_costs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Fuel className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fuel Costs</p>
                <p className="text-2xl font-heading font-bold">${data.total_fuel_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-xs text-muted-foreground">{data.total_fuel_liters.toLocaleString()}L total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Wrench className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Service Costs</p>
                <p className="text-2xl font-heading font-bold">${data.total_service_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Scissors className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Turf Produced</p>
                <p className="text-2xl font-heading font-bold">{data.total_turf_sqm.toLocaleString()} sqm</p>
                <p className="text-xs text-muted-foreground">{(data.total_turf_sqm / 10000).toFixed(2)} hectares</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Breakdown */}
        <Card data-testid="monthly-costs">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" /> Monthly Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.monthly.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No cost data recorded yet</p>
            ) : (
              <div className="space-y-3">
                {data.monthly.map((m) => (
                  <div key={m.month} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{m.month}</span>
                      <span className="font-mono font-semibold">${m.total.toFixed(2)}</span>
                    </div>
                    <div className="flex gap-0.5 h-5 rounded-full overflow-hidden bg-muted">
                      {m.fuel > 0 && (
                        <div
                          className="bg-amber-500 h-full rounded-l-full flex items-center justify-center text-[9px] text-white font-medium"
                          style={{ width: `${(m.fuel / maxMonthlyTotal) * 100}%`, minWidth: m.fuel > 0 ? '20px' : '0' }}
                          title={`Fuel: $${m.fuel.toFixed(2)}`}
                        >
                          {m.fuel >= 10 ? `$${m.fuel.toFixed(0)}` : ''}
                        </div>
                      )}
                      {m.services > 0 && (
                        <div
                          className="bg-blue-500 h-full rounded-r-full flex items-center justify-center text-[9px] text-white font-medium"
                          style={{ width: `${(m.services / maxMonthlyTotal) * 100}%`, minWidth: m.services > 0 ? '20px' : '0' }}
                          title={`Services: $${m.services.toFixed(2)}`}
                        >
                          {m.services >= 10 ? `$${m.services.toFixed(0)}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {m.fuel > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Fuel: ${m.fuel.toFixed(2)}</span>}
                      {m.services > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Services: ${m.services.toFixed(2)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Per Vehicle Costs */}
        <Card data-testid="vehicle-costs">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" /> Cost Per Vehicle
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.per_vehicle.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No vehicle cost data yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Fuel</TableHead>
                    <TableHead className="text-right">Services</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.per_vehicle.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell className="text-right font-mono">${v.fuel.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">${v.services.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">${v.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
